import { FormEvent, useEffect, useRef, useState } from 'react';
import { useFormApi, useFormState } from 'informed';
import { useReduxState } from 'hooks';
import { Alert } from 'components/common';
import { Checkbox, FormGroup, Select, Text } from 'components/common/form';

import type {
  CustomAttributes,
  ServerConfigProductCustomAttribute
} from 'shared-types';

interface Props {
  attribute: ServerConfigProductCustomAttribute;
  attributeIsFilled: boolean;
  errorLabel?: string;
  onChange?: (value: CustomAttributes) => void;
  options: string[];
  warnings?: { [key: string]: string[] };
}

const ProductCustomAttributesAttribute = ({
  attribute,
  attributeIsFilled,
  errorLabel,
  onChange,
  options = [],
  warnings = {}
}: Props) => {
  const { language, messages } = useReduxState();

  const formApi = useFormApi();
  const formState = useFormState();

  const onChangeTimeout = useRef<NodeJS.Timeout>(undefined);

  const [error, setError] = useState(undefined);
  const [optionsState, setOptionsState] = useState<string[]>([]);

  useEffect(() => {
    if (!optionsState.length) setOptionsState(options);
    else if (JSON.stringify(options) !== JSON.stringify(optionsState)) {
      // this means that the available options changed due to a change
      // in conditions, so we need to possibly edit the current field values
      const currentValues = formState.values?.customAttributes?.[attribute.key];
      if (Array.isArray(currentValues)) {
        // option field takes an array of values
        const validValues = currentValues.filter((value) =>
          options.includes(value)
        );
        formApi.setValue(`customAttributes.${attribute.key}`, validValues);
      } else if (!options.includes(currentValues)) {
        // option field takes a single value
        formApi.setValue(`customAttributes.${attribute.key}`, undefined);
      }

      setOptionsState(options);
    }
  }, [options]);

  const handleChange = (values: CustomAttributes['key']) => {
    onChange({ [attribute.key]: values });
  };

  const handleChangeWithThrottle = (values: CustomAttributes['key']) => {
    clearTimeout(onChangeTimeout.current);
    onChangeTimeout.current = setTimeout(
      () => onChange({ [attribute.key]: values }),
      300
    );
  };

  const validateCustomAttribute = (value: CustomAttributes['key']) => {
    const isEmpty =
      (value !== 0 && !value) || (Array.isArray(value) && !value.length);

    const error = isEmpty ? errorLabel : undefined;
    setError(error);
    return error;
  };

  const currentValue = formState.values?.customAttributes?.[attribute.key];
  const labelSimple = attribute.titleData?.[language] || attribute.title;
  const labelWithRequired = `${labelSimple} (${messages.commonRequired})`;
  const labelHelpText =
    attribute.descriptionTranslations?.[language] || attribute.description;

  const commonParams = {
    htmlFor: `custom-attributes-${attribute.key}`,
    label: attribute.required ? labelWithRequired : labelSimple,
    ...(labelHelpText ? { labelHelpText } : {})
  };

  const commonInputParams = {
    field: `customAttributes.${attribute.key}`,
    id: `custom-attributes-${attribute.key}`,
    ...(!attributeIsFilled ? { initialValue: attribute.defaultValue } : {}),
    ...(onChange ? { onChange: handleChange } : {}),
    ...(attribute.required
      ? { error, validate: validateCustomAttribute, validateOnChange: true }
      : {})
  };

  const isClearable = !attribute.required;

  return attribute.type === 'Array' ? (
    <FormGroup {...commonParams}>
      <Select
        {...commonInputParams}
        creatable={true}
        isMulti={true}
        isClearable={isClearable}
      />
    </FormGroup>
  ) : attribute.type === 'Boolean' ? (
    <FormGroup {...commonParams}>
      <Checkbox
        {...commonInputParams}
        label={labelSimple}
        onChange={(ev: FormEvent<HTMLInputElement>) =>
          handleChange(ev.currentTarget.checked)
        }
        style={{ display: 'inline-block', verticalAlign: 'top' }}
      />
    </FormGroup>
  ) : attribute.type === 'Number' ? (
    <FormGroup {...commonParams} maxWidth="120px">
      <Select
        {...commonInputParams}
        isClearable={isClearable}
        isSearchable={true}
        options={Array.from(
          Array(Math.max(attribute.max - attribute.min + 1, 1)),
          (_, i) => ({ label: i + attribute.min, value: i + attribute.min })
        )}
      />
    </FormGroup>
  ) : attribute.type.includes('WithOptions') ? (
    <FormGroup {...commonParams} maxWidth="360px">
      <Select
        {...commonInputParams}
        isClearable={isClearable}
        isSearchable={true}
        options={options.map((option) => ({ label: option, value: option }))}
        {...(attribute.type === 'ArrayWithOptions' ? { isMulti: true } : {})}
      />

      {formState.dirty && warnings[currentValue]?.length ? (
        <Alert
          size="small"
          style={{ margin: '5px 0' }}
          text={
            <div
              dangerouslySetInnerHTML={{
                __html: `${messages.commonFieldRequired}:<br/> ${warnings[
                  currentValue
                ].join('<br/>')}`
              }}
            />
          }
          type="warning-light"
        />
      ) : null}
    </FormGroup>
  ) : (
    <FormGroup {...commonParams}>
      <Text
        {...commonInputParams}
        onChange={handleChangeWithThrottle}
        type="text"
      />
    </FormGroup>
  );
};

export default ProductCustomAttributesAttribute;
